import { isEmpty } from 'lodash';
import i18next from 'i18next';
import React from 'react';

import { canEditBufferStocks } from '@selectors/actions/storeActions';
import { CONSUMPTION_FREQUENCY } from '@admin/commons/constants/nonSalesConsumption';
import { ConsumptionFrequency } from '@admin/stores/StoreDetailsModules/StoreBufferStocks/styledComponents';
import { translateUnit } from '@commons/utils/translateUnit';

export const getColumns = (currency) => [
  {
    id: 'ingredientName',
    name: i18next.t('GENERAL.NAME'),
    propertyKey: 'ingredientName',
    // The render method is necessary otherwise the text will overflow if the name is too long
    render: (item) => item,
  },
  {
    id: 'category',
    name: i18next.t('GENERAL.CATEGORY'),
    propertyKey: 'category',
    filterType: 'string',
    filterStringAllowNone: true,
    render: (item) => item || i18next.t('GENERAL.NONE_VALUE'),
  },
  {
    id: 'bufferStock',
    name: i18next.t('ADMIN.STORES.BUFFER_STOCK_COLUMN_TITLE'),
    tooltipText: i18next.t('ADMIN.STORES.BUFFER_STOCK_COLUMN_TOOLTIP'),
    propertyKey: 'bufferStock',
    filterType: 'numeric',
    excludeFromSearch: true,
    render: (days) => i18next.t('ADMIN.STORES.BUFFER_STOCK_COLUMN_VALUE', { days }),
  },
  {
    id: 'consumptionQuantity',
    name: i18next.t('ADMIN.STORES.CONSUMPTION_QUANTITY_COLUMN_TITLE'),
    tooltipText: i18next.t('ADMIN.STORES.CONSUMPTION_QUANTITY_COLUMN_TITLE_TOOLTIP'),
    propertyKey: 'consumptionQuantity',
    filterType: 'numeric',
    excludeFromSearch: true,
    render: (consumptionQuantity, { ingredientUnit }) =>
      `${consumptionQuantity || '-'} ${translateUnit(ingredientUnit)}`,
  },
  {
    id: 'consumptionFrequency',
    name: i18next.t('ADMIN.STORES.CONSUMPTION_FREQUENCY_COLUMN_TITLE'),
    tooltipText: i18next.t('ADMIN.STORES.CONSUMPTION_FREQUENCY_COLUMN_TITLE_TOOLTIP', {
      currency,
    }),
    propertyKey: 'consumptionFrequency',
    filterType: 'string',
    excludeFromSearch: true,
    render: (consumptionFrequency) => {
      if (!consumptionFrequency) {
        return '';
      }

      const isDailyConsumption = consumptionFrequency === CONSUMPTION_FREQUENCY.DAILY;

      return (
        <ConsumptionFrequency isDailyConsumption={isDailyConsumption}>
          {isDailyConsumption
            ? i18next.t('ADMIN.STORES.CONSUMPTION_FREQUENCY_DAILY')
            : i18next.t('ADMIN.STORES.CONSUMPTION_FREQUENCY_PER_1000_CURRENCY', {
                currency,
              })}
        </ConsumptionFrequency>
      );
    },
  },
];

export const getActions = ({
  openBufferStockModal,
  openNonSalesConsumptionModal,
  filteredIngredients,
  selectedIngredients,
  handleExportXLS,
  authorizedActions,
}) => {
  const areItemsSelected = !isEmpty(selectedIngredients);

  const actions = [];

  if (areItemsSelected) {
    if (canEditBufferStocks(authorizedActions)) {
      actions.push({
        id: 'manage-buffer-stock',
        actionLabel: () => i18next.t('ADMIN.STORES.MANAGE_BUFFER_STOCKS_ACTIONS'),
        handleAction: () => openBufferStockModal(selectedIngredients),
        actionIcon: () => '/images/inpulse/gear-black-small.svg',
      });
    }

    actions.push({
      id: 'manage-non-sales-consumption',
      actionLabel: () => i18next.t('ADMIN.STORES.MANAGE_NON_SALES_CONSUMPTION_ACTIONS'),
      handleAction: () => openNonSalesConsumptionModal(selectedIngredients),
      actionIcon: () => '/images/inpulse/gear-black-small.svg',
    });
  }

  actions.push({
    id: 'export-list',
    actionLabel: () =>
      i18next.t(areItemsSelected ? 'GENERAL.EXPORT_SELECTION' : 'GENERAL.EXPORT_LIST'),
    handleAction: () =>
      handleExportXLS(areItemsSelected ? selectedIngredients : filteredIngredients),
    actionIcon: () => '/images/inpulse/file-download-black-small.svg',
  });

  return actions;
};

export const getRowActions = ({
  openBufferStockModal,
  openNonSalesConsumptionModal,
  authorizedActions,
}) => {
  const actions = [];

  if (canEditBufferStocks(authorizedActions)) {
    actions.push({
      id: 'action-link',
      actionLabel: () => i18next.t('ADMIN.STORES.MANAGE_BUFFER_STOCKS_ACTIONS'),
      handleAction: (selectedIngredient) => openBufferStockModal([selectedIngredient]),
      actionIcon: () => '/images/inpulse/gear-black-small.svg',
    });
  }

  actions.push({
    id: 'manage-non-sales-consumption',
    actionLabel: () => i18next.t('ADMIN.STORES.MANAGE_NON_SALES_CONSUMPTION_ACTIONS'),
    handleAction: (selectedIngredient) => openNonSalesConsumptionModal([selectedIngredient]),
    actionIcon: () => '/images/inpulse/gear-black-small.svg',
  });

  return actions;
};

// Do not delete, this fake data is necessary to show the upgrade plan view
export const FAKE_DATA_FOR_PLAN_UPGRADE = [
  {
    id: 1,
    ingredientName: 'Pomme de terre',
    category: 'Féculent',
    bufferStock: 1,
  },
  {
    id: 2,
    ingredientName: 'Hamburger',
    category: 'Fast food',
    bufferStock: 15,
  },
  {
    id: 3,
    ingredientName: 'Glace',
    category: 'Froid',
    bufferStock: 3,
  },
  {
    id: 4,
    ingredientName: 'Pomme',
    category: 'Fruit',
    bufferStock: 2,
  },
  {
    id: 5,
    ingredientName: 'Orange',
    category: 'Fruit',
    bufferStock: 1,
  },
  {
    id: 6,
    ingredientName: 'Agrume',
    category: 'Fruit',
    bufferStock: 5,
  },
  {
    id: 7,
    ingredientName: 'Farine',
    category: null,
    bufferStock: 5,
  },
  {
    id: 8,
    ingredientName: 'Banane',
    category: 'Fruit',
    bufferStock: 5,
  },
  {
    id: 9,
    ingredientName: 'Pain',
    category: 'Boulangerie',
    bufferStock: 5,
  },
  {
    id: 10,
    ingredientName: 'Yaourt',
    category: 'Dessert',
    bufferStock: 5,
  },
];
